import { useNavigate } from 'react-router-dom'

import { Button, useToggle } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionConfig from 'modules/surveys/store/models/QuestionConfig'
import type Survey from 'modules/surveys/store/models/v2/Survey'

type ToggleData = {
  id: string
  surveys_form_id: string
  archived: boolean
}

interface Props {
  form: SurveyForm | Survey
  questionConfig: QuestionConfig
  toggleArchivedQuestionConfig: (data: ToggleData) => Promise<unknown>
  setOpenedModal: (state: ModalStates) => void
}

export default function Actions({
  form,
  questionConfig,
  toggleArchivedQuestionConfig,
  setOpenedModal,
}: Props) {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const [isOpen, toggleOpen] = useToggle(false)

  const updateAction = {
    onClick: () => setOpenedModal('edit'),
    title: i18n.t('surveys.form.question_configs.update'),
  }

  const archiveAction = {
    onClick: async () => {
      if (form.isActive) {
        if (questionConfig.attributes.archived) {
          return navigate(
            navigation.engagement.surveys.id.questions.unarchiveQuestionConfigId.fullPath({
              id: form.attributes.id,
              question_config_id: questionConfig.attributes.id,
            })
          )
        }

        return navigate(
          navigation.engagement.surveys.id.questions.archiveQuestionConfigId.fullPath({
            id: form.attributes.id,
            question_config_id: questionConfig.attributes.id,
          })
        )
      }

      await asyncAction(() => {
        const data = {
          id: questionConfig.attributes.id,
          surveys_form_id: form.id,
          archived: !questionConfig.attributes.archived,
        }

        return toggleArchivedQuestionConfig(data)
      })
    },
    title: questionConfig.attributes.archived
      ? i18n.t('surveys.form.question_configs.convert_to_undraft')
      : i18n.t('surveys.form.question_configs.convert_to_draft'),
  }

  const deleteAction = {
    onClick: () => setOpenedModal('delete'),
    type: 'wrong',
    title: i18n.t('surveys.form.question_configs.delete'),
  }

  const viewAction = {
    onClick: () => setOpenedModal('view'),
    title: i18n.t('surveys.form.question_configs.view'),
  }

  const duplicateAction = {
    onClick: () => setOpenedModal('duplicate'),
    title: i18n.t('surveys.form.question_configs.duplicate.title'),
  }

  const actions = (() => {
    if (form.isDraft) {
      return [updateAction, duplicateAction, archiveAction, deleteAction]
    }

    return [archiveAction, viewAction]
  })()

  return (
    <Popover attachment="top center" toggle={toggleOpen} open={isOpen}>
      <Button type="secondary" onClick={toggleOpen(true)} iconName="ellipsis" />
      <PopoverContent onClose={toggleOpen(false)} options={actions} />
    </Popover>
  )
}
